import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Flex, Form, Input, Radio, Select, Space, Typography, message } from 'antd'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import { useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import AuthSelectors from 'modules/domain/auth/selectors'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { getSeedInventoryCurrentMt, getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import { getSeedOrder, updateSeedOrder } from 'modules/domain/seedOrders/managers'
import { getSeedInventoryGrowerCompanies } from 'modules/domain/company/managers'
import { addNote } from 'modules/domain/note/managers'
import { NoteType } from 'types/entities'
import { useParams } from 'react-router-dom'
import { DestinationType, SeedOrderApiRecord, SeedOrderStatus } from 'modules/domain/seedOrders/types'
import Routes from './route'
import { seedTrackerVarietyLabel } from './SeedOrderAdd'
import CurrentGrowerInventoryCard from './CurrentGrowerInventoryCard'
import { getSeedOrderFarmerOptions } from './farmerOptions'
import { computeSeedOrderInventoryMtValues, SeedOrderInventoryMtValues } from './inventoryMt'
import {
  InputUnitSuffix,
  cardFieldStyle,
  fieldCellStyle,
  formMaxWidthStyle,
  pagePaddingStyle,
  sectionBlockStyle,
  sectionTitleStyle,
  seedOrderEditorFormColumnStyle,
  seedOrderEditorLayoutStyle,
  seedOrderEditorSidebarColumnStyle,
  sourceDestinationCardStyle,
  sourceDestinationCardsRowStyle,
  whiteContentStyle,
} from './styles'

export type SeedOrderEditFormValues = {
  order_status?: SeedOrderStatus
  product_card_id?: string
  sales_order_number?: string
  purchase_order_number?: string
  sales_invoice_number?: string
  current_pedigree?: SeedPedigree
  sold_as_pedigree?: SeedPedigree
  quantity_mt?: string
  origin_company_id?: string
  origin_location?: string
  destination_type?: DestinationType
  destination_company_id?: string
  destination_location?: string
  destination_farmer_id?: string
  destination_other_details?: string
  note_text?: string
}

const pedigreeOptions = Object.values(SeedPedigree).map((v) => ({ label: v, value: v }))
const destinationTypeOptions = Object.values(DestinationType).map((v) => ({ label: v, value: v }))
const statusOptions = Object.values(SeedOrderStatus)

/** Only the field matching the selected destination_type is kept; the others are nulled out. */
function buildDestinationFields(values: SeedOrderEditFormValues): Record<string, unknown> {
  const type = values.destination_type
  return {
    destination_company_id: type === DestinationType.Grower ? values.destination_company_id : null,
    destination_farmer_id: type === DestinationType.Farmer ? values.destination_farmer_id : null,
    destination_other_details: type === DestinationType.Other ? values.destination_other_details?.trim() || null : null,
  }
}

function buildUpdateSeedOrderBody(
  values: SeedOrderEditFormValues,
  inventoryValues: SeedOrderInventoryMtValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    product_card_id: values.product_card_id,
    origin_company_id: values.origin_company_id,
    destination_type: values.destination_type,
    ...buildDestinationFields(values),
    order_status: values.order_status,
    current_pedigree: values.current_pedigree,
    sold_as_pedigree: values.sold_as_pedigree,
    quantity_mt: values.quantity_mt!.trim(),
  }
  const optionalInventoryKeys: (keyof SeedOrderInventoryMtValues)[] = [
    'origin_current_mt',
    'origin_requested_mt',
    'origin_after_sale_mt',
    'destination_current_mt',
    'destination_requested_mt',
    'destination_after_sale_mt',
  ]
  for (const key of optionalInventoryKeys) {
    const raw = inventoryValues[key]
    if (typeof raw === 'string' && raw.trim() !== '') {
      body[key] = raw.trim()
    }
  }
  const optionalCodes: (keyof SeedOrderEditFormValues)[] = [
    'sales_order_number',
    'purchase_order_number',
    'sales_invoice_number',
  ]
  for (const key of optionalCodes) {
    const raw = values[key]
    if (typeof raw === 'string' && raw.trim() !== '') {
      body[key] = raw.trim()
    }
  }
  return body
}

const SeedOrderEdit: React.FC = () => {
  const push = useHistoryPush()
  const { t } = useTranslation(['seedTracker', 'common', 'validation', 'errors'])
  const profile = useSelector(AuthSelectors.profile)
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm<SeedOrderEditFormValues>()
  const [growerOptions, setGrowerOptions] = useState<{ label: string; value: string }[]>([])
  const [growersLoading, setGrowersLoading] = useState(false)
  const [varietyOptions, setVarietyOptions] = useState<{ label: string; value: string }[]>([])
  const [varietiesLoading, setVarietiesLoading] = useState(false)
  const [farmerOptions, setFarmerOptions] = useState<{ label: string; value: string }[]>([])
  const [farmersLoading, setFarmersLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [, setOrder] = useState<SeedOrderApiRecord | null>(null)
  const [originCurrentMt, setOriginCurrentMt] = useState<string | undefined>(undefined)
  const [destinationCurrentMt, setDestinationCurrentMt] = useState<string | undefined>(undefined)

  const goBack = useCallback(() => {
    push({ route: Routes.List })
  }, [push])

  useHelmet({ title: t('seedOrderEdit.metaTitle') })

  // Load the grower companies
  useEffect(() => {
    let cancelled = false
    setGrowersLoading(true)
    getSeedInventoryGrowerCompanies()
      .then((res) => {
        if (cancelled) {
          return
        }
        setGrowerOptions(
          (res.data ?? []).map((c) => ({
            value: c.id,
            label: c.official_name,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) {
          setGrowerOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGrowersLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Load the varieties
  useEffect(() => {
    let cancelled = false
    setVarietiesLoading(true)
    getSeedTrackersList({}, {}, 1, 5000)
      .then((res) => {
        if (cancelled) {
          return
        }
        const rows = res.data ?? []
        setVarietyOptions(
          rows.map((row) => ({
            value: row.id,
            label: seedTrackerVarietyLabel(row),
          })),
        )
      })
      .catch(() => {
        if (!cancelled) {
          setVarietyOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setVarietiesLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Load the farmers
  useEffect(() => {
    let cancelled = false
    setFarmersLoading(true)
    getSeedOrderFarmerOptions()
      .then((options) => {
        if (cancelled) {
          return
        }
        setFarmerOptions(options)
      })
      .catch(() => {
        if (!cancelled) {
          setFarmerOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFarmersLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Load the seed order data
  useEffect(() => {
    if (!id) {
      return
    }
    let cancelled = false
    setLoading(true)
    getSeedOrder(id)
      .then((res) => {
        if (cancelled) {
          return
        }
        setOrder(res)
        form.setFieldsValue({
          order_status: (res.order_status as SeedOrderStatus) ?? undefined,
          product_card_id: res.product_card_id,
          sales_order_number: res.sales_order_number ?? undefined,
          purchase_order_number: res.purchase_order_number ?? undefined,
          sales_invoice_number: res.sales_invoice_number ?? undefined,
          current_pedigree: res.current_pedigree,
          sold_as_pedigree: res.sold_as_pedigree,
          quantity_mt: res.quantity_mt,
          origin_company_id: res.origin_company_id,
          destination_type: res.destination_type ?? undefined,
          destination_company_id: res.destination_company_id,
          destination_farmer_id: res.destination_farmer_id ?? undefined,
          destination_other_details: res.destination_other_details ?? undefined,
        })
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('unknown_error', { ns: 'errors' }))
          goBack()
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [id, form, goBack, t])

  const handleFinish = useCallback(
    async (values: SeedOrderEditFormValues) => {
      if (!id) {
        return
      }
      const pcId = values.product_card_id?.trim()
      if (!pcId) {
        return
      }
      setSubmitting(true)
      try {
        const currentInventoryValues = computeSeedOrderInventoryMtValues({
          originCurrentMt,
          destinationCurrentMt,
          quantityMt: values.quantity_mt,
        })
        await updateSeedOrder(id, buildUpdateSeedOrderBody(values, currentInventoryValues))
        const noteText = values.note_text?.trim()
        const createdBy = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
        if (noteText) {
          try {
            await addNote({
              parent_id: id,
              note_type: NoteType.SeedOrderNote,
              note_text: noteText,
              created_by: createdBy || undefined,
            })
          } catch {
            void message.error('Order saved, but the note could not be saved.')
          }
        }
        message.success(t('seedOrderEdit.saveSuccess'))
        push({ route: Routes.List })
      } catch {
        message.error(t('unknown_error', { ns: 'errors' }))
      } finally {
        setSubmitting(false)
      }
    },
    [destinationCurrentMt, id, originCurrentMt, profile?.first_name, profile?.last_name, push, t],
  )

  const fk = (key: string) => ['seedOrderEdit', 'fields', key].join('.')
  const sk = (key: string) => ['seedOrderEdit', 'sections', key].join('.')

  const selectPlaceholder = t('select', { ns: 'common' })
  const requiredMsg = t('field_required', { ns: 'validation' })

  const selectedVarietyId = Form.useWatch('product_card_id', form)
  const selectedOriginCompanyId = Form.useWatch('origin_company_id', form)
  const selectedDestinationType = Form.useWatch('destination_type', form)
  const selectedDestinationCompanyId = Form.useWatch('destination_company_id', form)
  const selectedRequestedBu = Form.useWatch('quantity_mt', form)
  const selectedCurrentPedigree = Form.useWatch('current_pedigree', form)
  const selectedSoldAsPedigree = Form.useWatch('sold_as_pedigree', form)

  const selectedVarietyLabel = varietyOptions.find((o) => o.value === selectedVarietyId)?.label
  const selectedSourceLabel = growerOptions.find((o) => o.value === selectedOriginCompanyId)?.label
  const selectedDestinationLabel = growerOptions.find((o) => o.value === selectedDestinationCompanyId)?.label
  const inventoryMtValues = computeSeedOrderInventoryMtValues({
    originCurrentMt,
    destinationCurrentMt,
    quantityMt: selectedRequestedBu,
  })

  useEffect(() => {
    const productCardId = selectedVarietyId?.trim()
    const companyId = selectedOriginCompanyId?.trim()
    if (!productCardId || !companyId || !selectedCurrentPedigree) {
      setOriginCurrentMt(undefined)
      return
    }

    let cancelled = false
    getSeedInventoryCurrentMt({
      product_card_id: productCardId,
      company_id: companyId,
      seeded_pedigree: selectedCurrentPedigree,
    })
      .then((res) => {
        if (!cancelled) {
          setOriginCurrentMt(res.available_mt ?? undefined)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOriginCurrentMt(undefined)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedCurrentPedigree, selectedOriginCompanyId, selectedVarietyId])

  useEffect(() => {
    const productCardId = selectedVarietyId?.trim()
    const companyId = selectedDestinationCompanyId?.trim()
    if (!productCardId || !companyId || !selectedSoldAsPedigree) {
      setDestinationCurrentMt(undefined)
      return
    }

    let cancelled = false
    getSeedInventoryCurrentMt({
      product_card_id: productCardId,
      company_id: companyId,
      seeded_pedigree: selectedSoldAsPedigree,
    })
      .then((res) => {
        if (!cancelled) {
          setDestinationCurrentMt(res.available_mt ?? undefined)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDestinationCurrentMt(undefined)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedDestinationCompanyId, selectedSoldAsPedigree, selectedVarietyId])

  if (loading) {
    return (
      <>
        <Layout.Header>
          <Header.Root onClickBack={goBack}>
            <Header.Title title={t('seedOrderEdit.title')} />
          </Header.Root>
        </Layout.Header>
        <Layout.Content style={whiteContentStyle}>
          <div style={pagePaddingStyle}>{t('common:loading')}</div>
        </Layout.Content>
      </>
    )
  }

  return (
    <>
      <Layout.Header>
        <Header.Root onClickBack={goBack}>
          <Header.Title title={t('seedOrderEdit.title')} />
        </Header.Root>
      </Layout.Header>
      <Layout.Content style={whiteContentStyle}>
        <div style={pagePaddingStyle}>
          <div style={seedOrderEditorLayoutStyle}>
            <div style={seedOrderEditorFormColumnStyle}>
              <Form form={form} layout="vertical" onFinish={handleFinish} style={formMaxWidthStyle}>
                <div style={sectionBlockStyle}>
                  <Typography.Text type="secondary" style={sectionTitleStyle}>
                    Status
                  </Typography.Text>
                  <Form.Item
                    name="order_status"
                    rules={[{ required: true, message: requiredMsg }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Radio.Group buttonStyle="solid" size="large">
                      {statusOptions.map((status) => (
                        <Radio.Button key={status} value={status}>
                          {status}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </div>

                <div style={sectionBlockStyle}>
                  <Typography.Text type="secondary" style={sectionTitleStyle}>
                    {t(sk('referenceNumbers'))}
                  </Typography.Text>
                  <Flex gap={16} wrap align="flex-end">
                    <Form.Item name="sales_order_number" label={t(fk('salesOrderNumber'))} style={fieldCellStyle}>
                      <Input size="large" />
                    </Form.Item>
                    <Form.Item name="purchase_order_number" label={t(fk('purchaseOrderNumber'))} style={fieldCellStyle}>
                      <Input size="large" />
                    </Form.Item>
                    <Form.Item name="sales_invoice_number" label={t(fk('salesInvoiceNumber'))} style={fieldCellStyle}>
                      <Input size="large" />
                    </Form.Item>
                  </Flex>
                </div>

                <div style={sectionBlockStyle}>
                  <Typography.Text type="secondary" style={sectionTitleStyle}>
                    {t(sk('orderDetails'))}
                  </Typography.Text>
                  <Flex gap={16} wrap align="flex-end">
                    <Form.Item
                      name="product_card_id"
                      label={t(fk('variety'))}
                      style={fieldCellStyle}
                      rules={[{ required: true, message: requiredMsg }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={varietyOptions}
                        placeholder={selectPlaceholder}
                        size="large"
                        loading={varietiesLoading}
                      />
                    </Form.Item>
                    <Form.Item
                      name="current_pedigree"
                      label={t(fk('currentPedigree'))}
                      style={fieldCellStyle}
                      rules={[{ required: true, message: requiredMsg }]}
                    >
                      <Select allowClear options={pedigreeOptions} placeholder={selectPlaceholder} size="large" />
                    </Form.Item>
                    <Form.Item
                      name="sold_as_pedigree"
                      label={t(fk('soldAsPedigree'))}
                      style={fieldCellStyle}
                      rules={[{ required: true, message: requiredMsg }]}
                    >
                      <Select allowClear options={pedigreeOptions} placeholder={selectPlaceholder} size="large" />
                    </Form.Item>
                    <Form.Item
                      name="quantity_mt"
                      label={t(fk('quantityMt'))}
                      style={fieldCellStyle}
                      rules={[{ required: true, message: requiredMsg }]}
                    >
                      <Input size="large" suffix={<InputUnitSuffix>MT</InputUnitSuffix>} />
                    </Form.Item>
                  </Flex>
                </div>

                <div style={sectionBlockStyle}>
                  <Flex style={sourceDestinationCardsRowStyle} align="stretch">
                    <Card title={t(sk('source'))} style={sourceDestinationCardStyle}>
                      <Form.Item
                        name="origin_company_id"
                        label={t(fk('grower'))}
                        style={cardFieldStyle}
                        rules={[{ required: true, message: requiredMsg }]}
                      >
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={growerOptions}
                          placeholder={selectPlaceholder}
                          size="large"
                          loading={growersLoading}
                        />
                      </Form.Item>
                      {/*
                      <Form.Item name="origin_location" label={t(fk('location'))} style={{ marginBottom: 0 }}>
                        <Select disabled placeholder={t(fk('locationDisabledPlaceholder'))} size="large" options={[]} />
                      </Form.Item>
                      */}
                    </Card>

                    <Card title={t(sk('destination'))} style={sourceDestinationCardStyle}>
                      <Form.Item
                        name="destination_type"
                        label={t(fk('destinationType'))}
                        style={cardFieldStyle}
                        rules={[{ required: true, message: requiredMsg }]}
                      >
                        <Select
                          allowClear
                          options={destinationTypeOptions}
                          placeholder={selectPlaceholder}
                          size="large"
                          onChange={() => {
                            form.setFieldsValue({
                              destination_company_id: undefined,
                              destination_farmer_id: undefined,
                              destination_other_details: undefined,
                            })
                          }}
                        />
                      </Form.Item>
                      {selectedDestinationType === DestinationType.Grower && (
                        <Form.Item
                          name="destination_company_id"
                          label={t(fk('destinationName'))}
                          style={cardFieldStyle}
                          rules={[{ required: true, message: requiredMsg }]}
                        >
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={growerOptions}
                            placeholder={selectPlaceholder}
                            size="large"
                            loading={growersLoading}
                          />
                        </Form.Item>
                      )}
                      {/*
                      <Form.Item name="destination_location" label={t(fk('location'))} style={{ marginBottom: 0 }}>
                        <Select disabled placeholder={t(fk('locationDisabledPlaceholder'))} size="large" options={[]} />
                      </Form.Item>
                      */}
                      {selectedDestinationType === DestinationType.Farmer && (
                        <Form.Item
                          name="destination_farmer_id"
                          label={t(fk('destinationFarmerName'))}
                          style={cardFieldStyle}
                          rules={[{ required: true, message: requiredMsg }]}
                        >
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={farmerOptions}
                            placeholder={selectPlaceholder}
                            size="large"
                            loading={farmersLoading}
                          />
                        </Form.Item>
                      )}
                      {selectedDestinationType === DestinationType.Other && (
                        <Form.Item
                          name="destination_other_details"
                          label={t(fk('destinationOtherDetails'))}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: requiredMsg }]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      )}
                    </Card>
                  </Flex>
                </div>

                <div style={sectionBlockStyle}>
                  <Typography.Text type="secondary" style={sectionTitleStyle}>
                    Notes
                  </Typography.Text>
                  <Form.Item name="note_text" style={{ marginBottom: 0 }}>
                    <Input.TextArea placeholder="Add Note..." autoSize={{ minRows: 3, maxRows: 6 }} />
                  </Form.Item>
                </div>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" size="large" loading={submitting}>
                      {t('save', { ns: 'common' })}
                    </Button>
                    <Button onClick={goBack} size="large">
                      {t('cancel', { ns: 'common' })}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>

            <div style={seedOrderEditorSidebarColumnStyle}>
              <CurrentGrowerInventoryCard
                source={selectedSourceLabel}
                destination={selectedDestinationLabel}
                destinationType={selectedDestinationType}
                variety={selectedVarietyLabel}
                sourcePedigree={selectedCurrentPedigree}
                destinationPedigree={selectedSoldAsPedigree}
                originCurrentMt={inventoryMtValues.origin_current_mt}
                originRequestedMt={inventoryMtValues.origin_requested_mt}
                originAfterSaleMt={inventoryMtValues.origin_after_sale_mt}
                destinationCurrentMt={inventoryMtValues.destination_current_mt}
                destinationRequestedMt={inventoryMtValues.destination_requested_mt}
                destinationAfterSaleMt={inventoryMtValues.destination_after_sale_mt}
              />
            </div>
          </div>
        </div>
      </Layout.Content>
    </>
  )
}

export default SeedOrderEdit
